"use client";

import { useEffect } from "react";
import { useRouter, usePathname } from "next/navigation";
import { Loader2 } from "lucide-react";
import { usePortalSession } from "@/features/seo-portal/components/portal-session-provider";

export default function CabinetLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const { user, isLoading } = usePortalSession();
  const router = useRouter();
  const pathname = usePathname();

  /* <MEMENTO>2026-08-29: /cabinet/customer — кабинет заказчика с МЯГКИМ доступом:
  без сессии НЕ редиректим (страница сама показывает приглашение входа).
  Редирект на /login остаётся только для SEO-кабинета /cabinet.</MEMENTO> */
  /* <MEMENTO>oxpa-revive Фаза 0: /cabinet/skoro — «скоро»-страница кабинета,
  должна быть доступна всем (без сессии), чтобы CTA не уводили в мёртвую
  замороженную CRM. Если вложить её под /cabinet без исключения — layout
  редиректит на /login и «скоро» не видна.</MEMENTO> */
  const isPublicCabinet =
    (pathname?.startsWith("/cabinet/customer") ?? false) ||
    (pathname?.startsWith("/cabinet/skoro") ?? false);

  useEffect(() => {
    if (!isLoading && !user && !isPublicCabinet) {
      router.push("/login?redirect=/cabinet");
    }
  }, [isLoading, user, router, isPublicCabinet]);

  /* <MEMENTO>2026-09-08: фикс вечного спиннера на публичных кабинетах.
  Раньше isPublicCabinet учитывался только в редирект-эффекте и в ветке `!user`,
  а `if (isLoading)` стоял ПЕРВЫМ и рендерил спиннер для ВСЕХ /cabinet/*,
  включая публичные /cabinet/customer и /cabinet/skoro. Пока клиентский
  usePortalSession().isLoading === true, children не рендерились вовсе —
  реальный контент в RSC-пейлоаде есть, но заморожен за спиннером. Если сессия
  не резолвится (протухший токен у вернувшегося посетителя, недоступен auth
  замороженной CRM, медленная сеть) — isLoading остаётся true навсегда →
  вечный спиннер.
  Поэтому публичные кабинеты рендерят children СРАЗУ, не дожидаясь isLoading:
  isPublicCabinet проверяется ПЕРВЫМ. Исправление — не «отключить авторизацию»,
  а гарантировать отображение публичных страниц независимо от резолва сессии.
  НЕ УБИРАТЬ и НЕ переставлять ниже `if (isLoading)` — иначе «скоро»-страница
  снова замрёт на спиннере.</MEMENTO> */
  if (isPublicCabinet) {
    return <>{children}</>;
  }

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[hsl(var(--portal-text-muted))]" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[hsl(var(--portal-text-muted))]" />
      </div>
    );
  }

  return <>{children}</>;
}
